import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { TFunction } from 'i18next';

import { colors, distances, border } from '../../styles/constants';
import Card from '../../components/Card';
import { H3 } from '../../components/Typography';
import { OrderTravelItinerary, OrderItineraryAddress } from '../../types/orders';
import { DateTime } from '../../components/Formatters';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import { formatCountryCode } from '../../helpers/formatters';
import parseISO from 'date-fns/parseISO';
import Hr from '../../components/Hr';
export interface ItineraryProps extends WithTranslation {
    itinerary: OrderTravelItinerary[];
}

const getLegTitle = (leg: OrderTravelItinerary, t: TFunction) => {
    return [t([`orders.fields.itinerary.travel_modes.${leg.travel_mode}`, leg.travel_mode || '']), leg.operator]
        .filter((x) => x)
        .join(': ');
};

const getDistance = (leg: OrderTravelItinerary) => {
    if (leg.distance && leg.distance.value) {
        return {
            kilometers: Math.floor(leg.distance.value / 1000),
            meters: leg.distance.value % 1000,
            exact: true,
        };
    }
    if (
        leg.departure_address?.latitude &&
        leg.departure_address?.longitude &&
        leg.arrival_address?.latitude &&
        leg.arrival_address.longitude
    ) {
        // Not perfect, but better than nothing I guess
        // https://stackoverflow.com/questions/27928/calculate-distance-between-two-latitude-longitude-points-haversine-formula
        const p = 0.017453292519943295; // Math.PI / 180
        const c = Math.cos;
        const a =
            0.5 -
            c((leg.arrival_address.latitude - leg.departure_address.latitude) * p) / 2 +
            (c(leg.departure_address.latitude * p) *
                c(leg.arrival_address.latitude * p) *
                (1 - c((leg.arrival_address.longitude - leg.departure_address.longitude) * p))) /
                2;

        const meters = 12742000 * Math.asin(Math.sqrt(a)); // 2 * R; R = 6371000 m
        return {
            kilometers: Math.floor(meters / 1000),
            meters: meters % 1000,
            exact: false,
        };
    }
    return null;
};

const getDuration = (leg: OrderTravelItinerary) => {
    const minutes =
        (leg.duration && leg.duration.value) ||
        (leg.departure_at && leg.arrival_at
            ? differenceInMinutes(parseISO(leg.arrival_at), parseISO(leg.departure_at))
            : undefined
        );
    if (!minutes) {
        return null;
    }

    const hour = 60;
    const day = hour * 24;
    const result = {
        days: Math.floor(minutes / day),
        hours: Math.floor((minutes % day) / hour),
        minutes: minutes % hour,
    };
    if (result.days === 0 && result.hours === 0 && result.minutes === 0) {
        return null;
    }
    return result;
};

const getNameOfLocation = (location: OrderItineraryAddress) => {
    if (location.name) {
        return location.name;
    }
    const addressParts = [
        location.address_line,
        location.address_line_2,
        [location.postal_code, location.postal_place].filter((x) => x).join(' '),
        location.country && formatCountryCode(location.country),
    ].filter((x) => x);
    if (addressParts.length > 0) {
        return addressParts.join(', ');
    }
    if (location.id) {
        return location.id;
    }
    if (location.latitude && location.longitude) {
        return [location.latitude, location.longitude].join(', ');
    }
    return '';
};

interface ExternalId {
    id: string;
    source: string;
    description?: string;
}

const LocationIds = (
    {
        location,
    }: {
        location: OrderItineraryAddress;
    }
) => {
    return (
        <LocationIdsWrapper>
            {location.id && (
                <dl>
                    <dt>ID</dt>
                    <dt>{location.id}</dt>
                </dl>
            )}
            {(location.external_ids || []).map((ext: ExternalId) => (
                <>
                    <b>{ext.source}</b>:{ext.id} <i>{ext.description}</i>{' '}
                </>
            ))}
        </LocationIdsWrapper>
    );
};

const Location = (
    {
        location,
    }: {
        location: OrderItineraryAddress;
    }
) => {
    const hasLocation = location?.latitude && location?.longitude;
    const name = getNameOfLocation(location);
    if (hasLocation) {
        return (
            <>
                <StyledExternalLink
                    href={`https://maps.google.com/?q=${location.latitude},${location.longitude}`}
                    target="_blank"
                >
                    <span>{name}</span>
                    <sup>↗</sup>
                </StyledExternalLink>
                <LocationIds location={location} />
            </>
        );
    }
    return (
        <>
            {name}
            <LocationIds location={location} />
        </>
    );
};

const Itinerary = (
    {
        t,
        itinerary,
    }: ItineraryProps
) => {
    return (
        <Card marginBottom={distances.normal}>
            <H3>{t('orders.fields.itinerary.header')}</H3>
            <Hr />
            <div>
                {itinerary.sort((a, b) => ((new Date(a.departure_at ?? 0).getTime()) - (new Date(b.departure_at ?? 0).getTime()))).map((leg, i) => {
                    const duration = getDuration(leg);
                    const distance = getDistance(leg);

                    return (
                        <React.Fragment key={`fragment-${i}`}>
                            <LegWrapper key={i}>
                                <TimePlace>
                                    {leg.departure_at && <DateTime isoString={leg.departure_at} boldTime />}{' '}
                                    {leg.departure_address && <Location location={leg.departure_address} />}
                                </TimePlace>
                                <LegMain>
                                    <H3>{getLegTitle(leg, t)}</H3>
                                    {leg.summary && <Field>{leg.summary}</Field>}
                                    {leg.route_id && (
                                        <Field>
                                            {t('orders.fields.itinerary.route_id')} {leg.route_id}
                                        </Field>
                                    )}
                                    {leg.vehicle_id && (
                                        <Field>
                                            {t('orders.fields.itinerary.vehicle_id')} {leg.vehicle_id}
                                        </Field>
                                    )}
                                    {duration && (
                                        <Field>
                                            {[
                                                duration.days > 0 &&
                                                    t('orders.fields.itinerary.duration.day', { count: duration.days }),
                                                duration.hours > 0 &&
                                                    t('orders.fields.itinerary.duration.hour', {
                                                        count: duration.hours,
                                                    }),
                                                duration.minutes > 0 &&
                                                    t('orders.fields.itinerary.duration.minute', {
                                                        count: duration.minutes,
                                                    }),
                                            ]
                                                .filter((x) => x)
                                                .join(', ')}
                                            .
                                        </Field>
                                    )}
                                    {distance && (
                                        <Field>
                                            {!distance.exact && <>{t('orders.fields.itinerary.distance.not_exact')} </>}
                                            {distance.kilometers
                                                ? t('orders.fields.itinerary.distance.kilometer', {
                                                    count: distance.kilometers,
                                                })
                                                : t('orders.fields.itinerary.distance.meter', {
                                                    count: distance.meters,
                                                })}
                                            .
                                        </Field>
                                    )}
                                    {leg.line_ids && leg.line_ids.length && (
                                        <Field>
                                            {t('orders.fields.line_item_id', { count: leg.line_ids.length })}:{' '}
                                            {leg.line_ids.join(', ')}
                                        </Field>
                                    )}
                                    {leg.tickets && leg.tickets.length && (
                                        <>
                                            <Field>
                                                {t('orders.fields.itinerary.ticket.header', {
                                                    count: leg.tickets.length,
                                                })}
                                                :
                                            </Field>
                                            <Tickets>
                                                {leg.tickets.map((ticket, i) => (
                                                    <Ticket key={i}>
                                                        {ticket.class && (
                                                            <TicketLine>
                                                                {t('orders.fields.itinerary.ticket.class')}:{' '}
                                                                {ticket.class}
                                                            </TicketLine>
                                                        )}
                                                        {ticket.class && (
                                                            <TicketLine>
                                                                {t('orders.fields.itinerary.ticket.zone')}:{' '}
                                                                {ticket.zone}
                                                            </TicketLine>
                                                        )}
                                                        {ticket.line && (
                                                            <TicketLine>
                                                                {t('orders.fields.itinerary.ticket.line')}:{' '}
                                                                {ticket.line}
                                                            </TicketLine>
                                                        )}
                                                        {ticket.seat && (
                                                            <TicketLine>
                                                                {t('orders.fields.itinerary.ticket.seat')}:{' '}
                                                                {ticket.seat}
                                                            </TicketLine>
                                                        )}
                                                        {ticket.passenger_id && ticket.passenger_id.length && (
                                                            <TicketLine>
                                                                {t('orders.fields.itinerary.ticket.passenger_id', {
                                                                    count: ticket.passenger_id.length,
                                                                })}
                                                                : {ticket.passenger_id.join(', ')}
                                                            </TicketLine>
                                                        )}
                                                    </Ticket>
                                                ))}
                                            </Tickets>
                                        </>
                                    )}
                                </LegMain>
                                <TimePlace>
                                    {leg.arrival_at && <DateTime isoString={leg.arrival_at} boldTime />}{' '}
                                    {leg.arrival_address && <Location location={leg.arrival_address} />}
                                </TimePlace>
                            </LegWrapper>
                            {i !== itinerary.length - 1 && <LegSpacer />}
                        </React.Fragment>
                    );
                })}
            </div>
        </Card>
    );
};

export default withTranslation()(Itinerary);



const TimePlace = styled.div`
    position: relative;
    padding-top: 2px;
    width: 100%;

    &:after {
        position: absolute;
        top: 0px;
        left: -22px;
        content: ' ';
        height: ${distances.small};
        width: ${distances.small};
        border-radius: ${distances.small};
        background: rgba(242, 242, 242, 1);
    }
`;
const LocationIdsWrapper = styled.div`
    margin-left: 18px;
    display: inline-block;
    white-space: nowrap;
`;

const LegWrapper = styled.div`
    padding-top: 0;
    padding-left: ${distances.small};
    padding-bottom: 0;
    width: 100%;
    font-size: 11px;
    position: relative;
    color: #46535e;

    &:before {
        content: ' ';
        position: absolute;
        top: 10px;
        bottom: 10px;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
`;

const LegSpacer = styled.div`
    padding-top: 0;
    padding-left: ${distances.small};
    padding-bottom: 0;
    width: 100%;
    height: ${distances.normal};
    font-size: 11px;
    position: relative;
    color: #46535e;

    &:before {
        content: ' ';
        position: absolute;
        top: 0px;
        bottom: 0px;
        left: 0;
        width: 4px;
        border-left: 4px dotted rgba(242, 242, 242, 1);
    }
`;

const LegMain = styled.div`
    padding-top: ${distances.small};
    padding-bottom: ${distances.small};
    padding-left: ${distances.small};
    width: 100%;
    font-size: 11px;
    position: relative;
    color: #46535e;
`;

const Field = styled.div`
    display: block;
    word-break: normal;
    white-space: nowrap;
`;
const Tickets = styled.div`
    display: block;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
`;

const Ticket = styled.div`
    border: 2px dashed rgba(242, 242, 242, 1);
    padding: ${distances.small};
    margin: ${distances.small};
`;
const TicketLine = styled.div``;

const StyledExternalLink = styled.a`
    color: ${colors.primary};
    text-decoration: none;

    & > span {
        text-decoration: underline;
    }
    &:hover {
        color: ${colors.primaryHover};
    }
    & > sup {
        position: absolute;
        text-decoration: none;
    }
`;
